/* eslint-disable react/prop-types */
import { Box, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import InputField from "../../../components/common/new/InputField/InputField";
import { isEmptyObj } from "../../../utils/helper";
import useParamsQuery from "../../../hooks/useParamsQuery";
import DateRangePicker from "../../../components/common/DateRangePicker/DateRangePicker";
import AutoCompleteDropdown from "../../../components/common/new/AutoCompleteDropdown/AutoCompleteDropdown";

const initialFormValue = {
  startAtFrom: "",
  startAtTo: "",
  endAtFrom: "",
  endAtTo: "",
};

function PersonalDetailsSearch() {
  const { getQueryObject, getQueryParams, urlSearchParams } = useParamsQuery();
  const [initialAdvanceFormValue, setInitialAdvanceFormValue] =
    useState(initialFormValue);
  const [selectedType, setSelectedType] = useState("");
  const [fullName, setFullName] = useState("");
  const [memberId, setMemberId] = useState("");
  const [subscriptionNumber, setSubscriptionNumber] = useState("");

  const SUB_TYPE = [
    { name: "VC", value: "VC" },
    { name: "SS", value: "FDT" },
    { name: "EVC", value: "EVC" },
  ];

  useEffect(() => {
    const queryObject = getQueryObject();
    if (!isEmptyObj(queryObject)) {
      const query = getQueryParams(queryObject)[0];
      Object.keys(initialFormValue).forEach((key) => {
        setInitialAdvanceFormValue((prev) => ({
          ...prev,
          [key]: query[key] ?? "",
        }));
      });
      setFullName(query?.fullName || "");
      setMemberId(query?.memberId || "");
      setSubscriptionNumber(query?.subscriptionNumber || "");
      setSelectedType(query?.type || "");
    } else {
      setInitialAdvanceFormValue(initialFormValue);
      setFullName("");
      setMemberId("");
      setSubscriptionNumber("");
      setSelectedType("");
    }
  }, [urlSearchParams.toString()]);

  return (
    <Box
      sx={{
        width: "100%",
        pr: { xs: "20px", sm: 0 },
        // ✅ Global styling
        "& .MuiInputBase-root": {
          height: "40px !important",
          fontSize: "14px",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
        },
        "& .MuiInputLabel-root": {
          fontSize: "14px !important",
          fontWeight: 500,
        },
        "& .MuiInputLabel-shrink": {
          lineHeight: "1.4375em",
        },
        "& .MuiOutlinedInput-input": {
          padding: "8.5px 14px !important",
          height: "auto !important",
          fontSize: "14px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderRadius: "8px",
        },
        "& .MuiTextField-root": {
          "& .MuiInputBase-root": {
            height: "40px",
            alignItems: "center",
          },
        },
        "& .MuiInputBase-input": {
          height: "auto !important",
          padding: "8.5px 14px !important",
        },
        // ✅ Specific styling for DateRangePicker only
        "& .date-range-picker": {
          "& .MuiInputLabel-root": {
            lineHeight: "1",
            top: "-4px",
          },
          "& .MuiInputLabel-shrink": {
            top: "0px !important",
          },
        },
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(4, 1fr)",
          },
          rowGap: 2,
          columnGap: 2,
        }}
      >
        {/* Group 1: Member Details */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <InputField
            label="Member ID"
            name="memberId"
            value={memberId}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/\D/g, "");
              setMemberId(onlyNums);
            }}
          />
          <InputField
            label="Member Name"
            name="fullName"
            value={fullName}
            onChange={(e) => {
              const { value } = e.target;
              const regex = /^[A-Za-z ]*$/;
              if (regex.test(value)) {
                setFullName(value);
              }
            }}
          />
        </Grid>

        {/* Group 2: Subscription Details */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <AutoCompleteDropdown
            getOptionLabel={(option) => option.name}
            label="Subscription Type"
            name="type"
            options={SUB_TYPE}
            value={selectedType}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setSelectedType("");
              } else {
                setSelectedType(selectedValue.value);
              }
            }}
          />
          {/* ✅ Hidden input for form submission */}
          <input type="hidden" name="type" value={selectedType || ""} />
          <InputField
            label="Subscription Number"
            name="subscriptionNumber"
            value={subscriptionNumber}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/\D/g, "");
              setSubscriptionNumber(onlyNums);
            }}
          />
        </Grid>

        {/* Group 3: Date Range */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <Box className="date-range-picker">
            <DateRangePicker
              label="Start Date"
              name="startAt"
              startDateVal={initialAdvanceFormValue.startAtFrom}
              endDateVal={initialAdvanceFormValue.startAtTo}
            />
          </Box>
          <Box className="date-range-picker">
            <DateRangePicker
              label="End Date"
              name="endAt"
              startDateVal={initialAdvanceFormValue.endAtFrom}
              endDateVal={initialAdvanceFormValue.endAtTo}
            />
          </Box>
        </Grid>

        {/* Group 4: Empty */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          {/* Empty group for future use */}
        </Grid>
      </Grid>
    </Box>
  );
}

export default PersonalDetailsSearch;
